import _dayjs from 'dayjs';
import {
  Car,
  Claim,
  Glass,
  GlassImpact,
  ImpactSize,
  ImpactType,
  OperationType,
  SinisterGlass,
} from '../models';

export const getOperationType = (glass: SinisterGlass): OperationType => {
  if (
    glass?.glass === 'windshield' &&
    canRepairImpactsType(glass?.impacts?.impactsType) &&
    canRepairImpactSize(glass?.impacts?.size) &&
    canRepairImpactFieldView(glass?.impacts)
  ) {
    return 'Repair';
  } else if (
    (!!glass?.glass && glass?.glass !== 'windshield') ||
    (glass?.glass === 'windshield' &&
      (!canRepairImpactsType(glass?.impacts?.impactsType) ||
        !canRepairImpactSize(glass?.impacts?.size) ||
        !canRepairImpactFieldView(glass?.impacts)))
  ) {
    return 'Replace';
  } else return null;
};

export const canRepairImpactsType = (impactType: ImpactType): boolean => {
  switch (impactType) {
    case '1impact':
    case '2impactsLess':
    case '2or3impacts':
    case '3impactsLess':
      return true;
    case '3impactsMore':
    case 'crack':
    case 'more3impactsOrCrack':
      return false;
    default:
      return undefined;
  }
};

export const canRepairImpactSize = (impactSize: ImpactSize): boolean => {
  return impactSize === 'smaller2cm';
};

export const canRepairImpactFieldView = (impact: GlassImpact): boolean => {
  return impact.atOuterEdge === false && impact.inVisionField === false;
};

export const getImpactNumber = (glass: SinisterGlass): string => {
  return glass?.impacts?.impactsType;
};

export const getImpactSize = (glass: SinisterGlass): string => {
  return glass?.impacts?.size;
};

export const getImpactInFieldOfView = (glass: SinisterGlass): boolean => {
  return glass?.impacts?.inVisionField;
};

export const getGlassNameGender = (glass: SinisterGlass): string => {
  if (
    glass?.glass === 'rearWindow' ||
    glass?.glass === 'sideWindow' ||
    glass?.glass === 'topWindow'
  ) {
    return `female`;
  }

  return `male`;
};

export const getGlassNameKey = (glass: SinisterGlass): string => {
  if (glass?.glass === 'sideWindow' && glass?.lateralPrecisions) {
    if (
      glass.lateralPrecisions.frontConductor &&
      glass.lateralPrecisions.precision
    ) {
      return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.lateralPrecisions?.precision}${glass?.lateralPrecisions?.frontConductor}`;
    } else if (
      !glass.lateralPrecisions.frontConductor &&
      glass.lateralPrecisions.precision
    ) {
      return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.lateralPrecisions?.precision}`;
    } else if (
      glass.lateralPrecisions.frontConductor &&
      !glass.lateralPrecisions.precision
    ) {
      return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.lateralPrecisions?.frontConductor}`;
    }
  } else if (glass?.glass === 'lights' && glass?.light) {
    if (glass?.light?.lateral && glass?.light?.lightsEmplacement) {
      return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.light?.lightsEmplacement}${glass?.light?.lateral}`;
    } else if (!glass?.light?.lateral && glass?.light?.lightsEmplacement) {
      return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.light?.lightsEmplacement}`;
    } else if (glass?.light?.lateral && !glass?.light?.lightsEmplacement) {
      return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.light?.lateral}`;
    }
  } else if (glass?.glass === 'mirror' && glass?.mirror) {
    return `GlobalApplicativeConstants.sinisterGlass.glass.${glass?.glass}.${glass?.mirror}`;
  }

  return `GlobalApplicativeConstants.sinisterGlass.glass.${
    glass?.glassExtended || glass?.glass
  }.name`;
};

export const getGlassNameDefaultKey = (
  glass: Glass | SinisterGlass
): string => {
  if (typeof glass === 'string') {
    return `GlobalApplicativeConstants.sinisterGlass.glass.${glass}.name`;
  }
  return `GlobalApplicativeConstants.sinisterGlass.glass.${
    glass?.glassExtended || glass?.glass
  }.name`;
};

export const getOperationWithGlassKey = (operationType: string) => {
  let key = 'GlobalApplicativeConstants.operationType.replaceChoiceOperation';

  if (operationType === 'Repair') {
    key = 'GlobalApplicativeConstants.operationType.repairChoiceOperation';
  }

  return key;
};

export const getOperationWithGenderedGlassKey = (operationType: string) => {
  let key =
    'GlobalApplicativeConstants.operationType.replaceChoiceOperationWithGender';

  if (operationType === 'Repair') {
    key =
      'GlobalApplicativeConstants.operationType.repairChoiceOperationWithGender';
  }

  return key;
};

export const getOperationDefaultKey = (operationType: string) => {
  let key = 'GlobalApplicativeConstants.operationType.replaceTitle';

  if (operationType === 'Repair') {
    key = 'GlobalApplicativeConstants.operationType.repairTitle';
  }

  return key;
};

export const isOperationTypeChanged = (
  oldClaim: Partial<Claim>,
  newClaim: Partial<Claim>
) => {
  return (
    oldClaim?.glass &&
    getOperationType(oldClaim.glass) === getOperationType(newClaim.glass)
  );
};

export const getCurrentCarDescription = (
  currentCar: Car,
  hasFeature: boolean,
  doorsLabel: string,
  dayjs: typeof _dayjs
) => {
  let carInfos = '';
  carInfos += currentCar?.manufacturer ? currentCar?.manufacturer : '';

  carInfos += currentCar?.model ? ' ' + currentCar?.model : '';
  carInfos += currentCar?.characteristics?.numberOfDoors
    ? ', ' + currentCar?.characteristics?.numberOfDoors + doorsLabel
    : '';

  if (hasFeature) {
    const registrationYear = dayjs(currentCar?.firstRegistration);
    carInfos = [
      currentCar?.model,
      currentCar?.manufacturer,
      currentCar?.version,
      registrationYear?.isValid() ? registrationYear?.format('YYYY') : '',
    ]
      .filter((value) => value)
      .join(' ');
  }
  return carInfos;
};

export const getGlassPrecisionsForTagManagerService = (
  sinisterGlass: SinisterGlass
) => {
  if (sinisterGlass?.glass === 'windshield') {
    if (sinisterGlass?.impacts?.impactsType) {
      if (sinisterGlass?.impacts?.size) {
        if (
          sinisterGlass?.impacts?.atOuterEdge ||
          sinisterGlass?.impacts?.inVisionField ||
          (sinisterGlass?.impacts?.atOuterEdge === false &&
            sinisterGlass?.impacts?.inVisionField === false)
        ) {
          return {
            glass_type: sinisterGlass?.glass,
            impact_type: sinisterGlass?.impacts?.impactsType,
            impact_size: sinisterGlass?.impacts?.size,
            red_zone_impact:
              sinisterGlass?.impacts?.atOuterEdge ||
              sinisterGlass?.impacts?.inVisionField
                ? 'yes'
                : 'no',
          };
        } else
          return {
            glass_type: sinisterGlass?.glass,
            impact_type: sinisterGlass?.impacts?.impactsType,
            impact_size: sinisterGlass?.impacts?.size,
          };
      } else
        return {
          glass_type: sinisterGlass?.glass,
          impact_type: sinisterGlass?.impacts?.impactsType,
        };
    } else
      return {
        glass_type: sinisterGlass?.glass,
      };
  } else if (sinisterGlass?.glass === 'mirror') {
    return {
      glass_type: sinisterGlass?.glass,
      window_side: sinisterGlass?.mirror,
    };
  } else if (sinisterGlass?.glass === 'lights') {
    return {
      glass_type: sinisterGlass?.glass,
      window_side: sinisterGlass?.light?.lateral,
      window_type: sinisterGlass?.light?.lightsEmplacement,
    };
  } else if (sinisterGlass?.glass === 'sideWindow') {
    return {
      glass_type: sinisterGlass?.glass,
      window_side: sinisterGlass?.lateralPrecisions?.frontConductor,
      window_type: sinisterGlass?.lateralPrecisions?.precision,
    };
  } else
    return {
      glass_type: sinisterGlass?.glass,
    };
};
